import {
  TrendingDown as DownIcon,
  TrendingFlat as FlatIcon,
  TrendingUp as UpIcon
} from '@mui/icons-material';

export const SMART_STATUS_COLORS = {
  Excelente: '#81c784',
  'Muito Bom': '#aed581',
  Bom: '#dce775',
  Regular: '#ffd54f',
  Alerta: '#ffb74d',
  Irregular: '#ff8a65',
  Ruim: '#e57373',
  'Muito Ruim': '#f06292',
  Pessimo: '#ba68c8'
};

export const COMPONENT_LABELS = {
  sentiment: 'Sentimento',
  exposure: 'Exposição',
  tier: 'Tier',
  reach: 'Abrangência',
  editorialOccupancy: 'Ocupação editorial',
  highlight: 'Destaque',
  spokesperson: 'Porta-voz',
  keyMessage: 'Mensagem-chave',
  editorialOrigin: 'Origem editorial'
};

export const SMART_STATUS_RANGES = [
  { label: 'Excelente', range: '+9.1 a +10', color: '#81c784' },
  { label: 'Muito Bom', range: '+6.1 a +9.0', color: '#aed581' },
  { label: 'Bom', range: '+4.1 a +6.0', color: '#dce775' },
  { label: 'Regular', range: '+2.1 a +4.0', color: '#ffd54f' },
  { label: 'Alerta', range: '-2.0 a +2.0', color: '#ffb74d' },
  { label: 'Irregular', range: '-4.0 a -2.1', color: '#ff8a65' },
  { label: 'Ruim', range: '-6.0 a -4.1', color: '#e57373' },
  { label: 'Muito Ruim', range: '-9.0 a -6.1', color: '#f06292' },
  { label: 'Pessimo', range: '-10 a -9.1', color: '#ba68c8' }
];

export const SENTIMENT_LABELS = {
  positive: 'Positivo',
  neutral: 'Neutro',
  negative: 'Negativo'
};

export const EXPOSURE_LABELS = {
  dedicated: 'Dedicada',
  shared: 'Compartilhada',
  mention: 'Citação',
  highlight: 'Highlight',
  none: 'Sem exposição'
};

const normalizeRawKey = (value) => String(value || '').trim().toLowerCase();

const isExposureComponent = (key) => (
  ['exposure', 'brand_exposure_type'].includes(normalizeRawKey(key))
);

export const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const clamp = (value, min = -10, max = 10) => Math.min(max, Math.max(min, toNumber(value)));

export const formatSigned = (value, digits = 1) => {
  const numeric = toNumber(value);
  return `${numeric > 0 ? '+' : ''}${numeric.toFixed(digits)}`;
};

export const formatPercent = (value) => {
  const numeric = toNumber(value);
  if (numeric <= 0) return '0%';
  return `${Math.round(numeric <= 1 ? numeric * 100 : numeric)}%`;
};

export const formatComponentRaw = (key, rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') return null;
  const normalizedRaw = normalizeRawKey(rawValue);
  if (key === 'sentiment') return SENTIMENT_LABELS[normalizedRaw] || null;
  if (isExposureComponent(key)) return EXPOSURE_LABELS[normalizedRaw] || null;
  if (key === 'tier') return rawValue ? `Tier ${rawValue}` : null;
  if (key === 'editorialOccupancy') return formatPercent(rawValue);
  if (key === 'highlight') return rawValue ? 'Sim' : 'Não';
  if (Array.isArray(rawValue)) return rawValue.length ? `${rawValue.length} itens` : 'Nenhum';
  if (typeof rawValue === 'boolean') return rawValue ? 'Sim' : 'Não';
  return String(rawValue);
};

export const getOccupancy = (article) => article?.editorialOccupancy || article?.onlineCentimetragem || {};

export const getEntityBreakdown = (article) => {
  const occupancy = getOccupancy(article);
  return Array.isArray(occupancy.entityBreakdown)
    ? occupancy.entityBreakdown.filter(entity => entity?.name)
    : [];
};

export const normalizeName = (name) => (
  String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
);

export const getSmartColor = (smartScore) => (
  SMART_STATUS_COLORS[smartScore?.status] || '#607D8B'
);

export const getSmartStatusForScore = (score) => {
  const numeric = Number(toNumber(score).toFixed(1));
  if (numeric >= 9.1) return 'Excelente';
  if (numeric >= 6.1) return 'Muito Bom';
  if (numeric >= 4.1) return 'Bom';
  if (numeric >= 2.1) return 'Regular';
  if (numeric >= -2.0) return 'Alerta';
  if (numeric >= -4.0) return 'Irregular';
  if (numeric >= -6.0) return 'Ruim';
  if (numeric >= -9.0) return 'Muito Ruim';
  return 'Péssimo';
};

export const getSmartBarColor = (score, sentiment) => {
  if (score <= 0) return '#78909C';

  if (sentiment === 'negative') {
    if (score >= 9.1) return '#C70321';
    if (score >= 6.1) return '#f06292';
    if (score >= 4.1) return '#e57373';
    if (score >= 2.1) return '#ff8a65';
    return '#ffb74d';
  }

  if (score >= 9.1) return '#81c784';
  if (score >= 6.1) return '#aed581';
  if (score >= 4.1) return '#dce775';
  if (score >= 2.1) return '#ffd54f';
  return '#ffb74d';
};

export const getCompetitionConfig = (score) => {
  if (score >= 3.1) {
    return {
      color: score >= 7.1 ? '#1B5E20' : '#2E7D32',
      icon: UpIcon,
      label: score >= 7.1 ? 'Vantagem absoluta' : 'Vantagem leve'
    };
  }

  if (score <= -3.1) {
    return {
      color: score <= -7.1 ? '#B71C1C' : '#C62828',
      icon: DownIcon,
      label: score <= -7.1 ? 'Desvantagem crítica' : 'Desvantagem leve'
    };
  }

  return {
    color: '#607D8B',
    icon: FlatIcon,
    label: 'Empate técnico'
  };
};

export const buildQualitativeText = (article) => {
  const occupancy = getOccupancy(article);
  if (occupancy.comparativeAnalysis) return occupancy.comparativeAnalysis;

  const entities = getEntityBreakdown(article)
    .filter(entity => (entity.participationPercent || 0) > 0 || (entity.areaCm2 || 0) > 0)
    .sort((a, b) => toNumber(b.participationPercent || b.areaCm2) - toNumber(a.participationPercent || a.areaCm2));

  if (entities.length > 0) {
    const [leader, ...others] = entities;
    const leaderExposure = EXPOSURE_LABELS[leader.exposure] || 'presença editorial';
    const leaderSentiment = SENTIMENT_LABELS[leader.sentiment] || 'neutro';
    const rest = others.slice(0, 2).map(entity => `${entity.name} (${formatPercent(entity.participationPercent)})`);
    return `${leader.name} lidera a presença editorial com ${formatPercent(leader.participationPercent)}, exposição ${leaderExposure.toLowerCase()} e sentimento ${leaderSentiment.toLowerCase()}${rest.length ? `. Também aparecem ${rest.join(' e ')}` : ''}.`;
  }

  if (article?.exposure || article?.sentiment) {
    const exposure = EXPOSURE_LABELS[article.exposure] || 'presença editorial';
    const sentiment = SENTIMENT_LABELS[article.sentiment] || 'neutro';
    return `A publicação registra exposição ${String(exposure).toLowerCase()} com sentimento ${String(sentiment).toLowerCase()}.`;
  }

  return null;
};

export const buildEntityScoreRows = (article) => {
  const entities = getEntityBreakdown(article);
  const smartScore = article?.smartScore;
  const competitorScore = article?.competitorScore;
  const rowsByKey = new Map();
  const findEntity = (name) => {
    const key = normalizeName(name);
    return entities.find(entity => normalizeName(entity.name) === key)
      || entities.find(entity => normalizeName(entity.name).includes(key) || key.includes(normalizeName(entity.name)));
  };

  const addRow = (row) => {
    const key = normalizeName(row.name) || `${row.type}-${rowsByKey.size}`;
    const existing = rowsByKey.get(key);
    const merged = {
      ...existing,
      ...row,
      entity: row.entity || existing?.entity,
      components: row.components?.length ? row.components : existing?.components || []
    };

    if (row.score === undefined && existing?.score !== undefined) merged.score = existing.score;
    if (!row.status && existing?.status) merged.status = existing.status;
    if (row.impactScore === undefined && existing?.impactScore !== undefined) merged.impactScore = existing.impactScore;
    if (row.polarity === undefined && existing?.polarity !== undefined) merged.polarity = existing.polarity;
    if (!row.source && existing?.source) merged.source = existing.source;

    rowsByKey.set(key, merged);
  };

  const brandEntity = entities.find(entity => entity.type === 'brand') || entities[0];
  if (brandEntity || smartScore?.enabled || competitorScore?.brandScore !== undefined) {
    addRow({
      name: brandEntity?.name || 'Marca monitorada',
      type: brandEntity?.type || 'brand',
      role: brandEntity?.type === 'competitor' ? 'Concorrente' : 'Cliente / marca',
      score: smartScore?.enabled ? smartScore.score : competitorScore?.brandScore,
      status: smartScore?.status || getSmartStatusForScore(competitorScore?.brandScore),
      impactScore: smartScore?.impactScore,
      polarity: smartScore?.polarity,
      components: smartScore?.components || [],
      entity: brandEntity,
      source: 'smartScore'
    });
  }

  if (Array.isArray(competitorScore?.competitors)) {
    competitorScore.competitors.forEach((competitor) => {
      const entity = findEntity(competitor.name);
      addRow({
        name: competitor.name || entity?.name || 'Concorrente',
        type: entity?.type || 'competitor',
        role: entity?.type === 'brand' ? 'Cliente / marca' : 'Concorrente',
        score: competitor.score,
        status: getSmartStatusForScore(competitor.score),
        impactScore: competitor.impactScore,
        polarity: competitor.polarity,
        components: competitor.components || [],
        entity,
        source: 'competitorScore'
      });
    });
  }

  entities.forEach((entity) => {
    addRow({
      name: entity.name,
      type: entity.type || 'entity',
      role: entity.type === 'brand' ? 'Cliente / marca' : entity.type === 'competitor' ? 'Concorrente' : 'Marca',
      entity
    });
  });

  return Array.from(rowsByKey.values())
    .filter(row => row.name)
    .sort((a, b) => {
      const aHasScore = Number.isFinite(Number(a.score));
      const bHasScore = Number.isFinite(Number(b.score));
      if (aHasScore && bHasScore) return toNumber(b.score) - toNumber(a.score);
      if (aHasScore) return -1;
      if (bHasScore) return 1;
      return toNumber(b.entity?.participationPercent || b.entity?.areaCm2) - toNumber(a.entity?.participationPercent || a.entity?.areaCm2);
    });
};
